import { PAYMENT_METHODS } from '@workflow/contracts'
import type { IdentityType, PaymentMethod, SubmitProfileInput } from '@workflow/contracts'

/**
 * The profile, as the company asks for it — the one list both the form and
 * the read-only views render from, so what is requested, what is reviewed and
 * what is on file can never drift apart. Field labels are the company's own
 * words for these. For a player, this profile IS the player record: the
 * Discord section is the stable key the outside world knows them by.
 */
export interface ProfileField {
  key: keyof SubmitProfileInput
  label: string
  placeholder?: string
  /** Spans the full row in the form grid. */
  wide?: boolean
  /** A control other than the plain text field. */
  kind?: 'textarea' | 'identity' | 'date' | 'payment'
}

export interface ProfileGroup {
  title: string
  fields: readonly ProfileField[]
}

export type ProfileFormState = Omit<SubmitProfileInput, 'paymentMethod'> & { paymentMethod: PaymentMethod | '' }

export const PROFILE_GROUPS: readonly ProfileGroup[] = [
  {
    title: 'Discord Info',
    fields: [
      { key: 'discordDisplayName', label: 'Display Name' },
      { key: 'discordUsername', label: 'Discord Username' },
      { key: 'discordUserId', label: 'Discord User ID (UID)', placeholder: 'Digits only' },
    ],
  },
  {
    title: 'Personal Information',
    fields: [
      { key: 'fullName', label: 'Full Name' },
      { key: 'currentAddress', label: 'Current Address', wide: true, placeholder: 'Street, area, city and postal code' },
      { key: 'dateOfBirth', label: 'Date of Birth', kind: 'date' },
    ],
  },
  {
    title: 'Contact Information',
    fields: [
      { key: 'primaryPhone', label: 'Primary Phone Number' },
      { key: 'emergencyContactName', label: 'Emergency Contact Name' },
      { key: 'emergencyContactRelationship', label: 'Emergency Contact Relationship' },
      { key: 'emergencyContactNumber', label: 'Emergency Contact Number' },
    ],
  },
  {
    title: 'Identity / Verification',
    fields: [
      { key: 'identityType', label: 'Document', kind: 'identity' },
      { key: 'identityNumber', label: 'NID / Passport Number' },
    ],
  },
  {
    title: 'Payment Information',
    fields: [
      { key: 'paymentMethod', label: 'Payment Method', kind: 'payment' },
      { key: 'paymentBankName', label: 'Bank Name' },
      { key: 'paymentAccountNumber', label: 'Account Number' },
      { key: 'paymentDetails', label: 'Details', wide: true, kind: 'textarea' },
    ],
  },
]

export const IDENTITY_TYPE_LABEL: Record<IdentityType, string> = {
  nid: 'NID',
  passport: 'Passport',
}

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  bank: 'Bank',
  bKash: 'bKash',
  Nagad: 'Nagad',
  custom: 'Custom',
}

export function isPaymentMethod(value: string): value is PaymentMethod {
  return PAYMENT_METHODS.some((method) => method === value)
}

export function paymentFieldVisible(key: keyof SubmitProfileInput, method: string, accountNumber = ''): boolean {
  if (key === 'paymentBankName') return method === 'bank'
  if (key === 'paymentAccountNumber') return method !== '' && (method !== 'custom' || accountNumber !== '')
  if (key === 'paymentDetails') return method === 'bank' || method === 'custom'
  return true
}

/** A profile value as it reads: the document by name, an absence as a dash. */
export function profileFieldValue(profile: unknown, field: ProfileField): string {
  const value = (profile as Record<string, unknown>)[field.key]
  if (field.key === 'identityType') {
    return typeof value === 'string' && value in IDENTITY_TYPE_LABEL
      ? IDENTITY_TYPE_LABEL[value as IdentityType]
      : '—'
  }
  if (field.key === 'paymentMethod' && typeof value === 'string' && isPaymentMethod(value)) {
    return PAYMENT_METHOD_LABEL[value]
  }
  return typeof value === 'string' && value ? value : '—'
}

/** The empty form, typed as the submission itself. */
export function emptyProfileForm(): ProfileFormState {
  return {
    discordDisplayName: '',
    discordUsername: '',
    discordUserId: '',
    fullName: '',
    currentAddress: '',
    dateOfBirth: '',
    primaryPhone: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactNumber: '',
    identityType: 'nid',
    identityNumber: '',
    paymentMethod: '',
    paymentAccountNumber: '',
    paymentBankName: '',
    paymentDetails: '',
  }
}
